import React from "react";
import { motion } from "framer-motion";
import Bin from "../../assets/svg/Bin.svg";
import Tooltip from "../reusable/Tooltip";
import { trimString } from "../../utils/utils";
import {
  getNotificationNavigation,
  handleNotificationNavigation,
  getNotificationDisplay,
  isUrgentNotification,
  getNotificationIcon,
} from "../../utils/notificationUtils";
import { API } from "../../shared/api/Api";
import axios from "axios";

const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
};

const NotificationRow = ({ notification, user, navigate, onDelete, onReload }) => {
  const lang = user?.lang || "en";
  
  // Parse notification options
  let parsedOptions = {};
  try {
    parsedOptions = notification.options ? JSON.parse(notification.options) : {};
  } catch (error) {
    console.error("Failed to parse notification options:", error);
  }

  // Get navigation and display data
  const navigationData = getNotificationNavigation(parsedOptions, user);
  const displayConfig = getNotificationDisplay(parsedOptions, user);
  const isUrgent = isUrgentNotification(parsedOptions);
  const icon = getNotificationIcon(parsedOptions);
  const isClicked = notification.pivot?.isClicked || false;

  /**
   * Mark notification as clicked
   */
  const handleNotificationClicked = async (notificationId) => {
    if (isClicked) return; // Already clicked

    try {
      const { data } = await axios.put(
        `${API.Notifications.notificationStatusUpdate}/${notificationId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            lang: lang,
          },
        }
      );

      if (data.status !== 200 || data.errorCode === 1) {
        console.error("Failed to mark notification as clicked:", data.message);
      }
    } catch (error) {
      console.error("Error marking notification as clicked:", error);
    }
  };

  /**
   * Handle notification click and navigation
   */
  const handleNotificationClick = async () => {
    // Mark as clicked
    await handleNotificationClicked(notification.id);

    // Navigate based on notification type
    if (navigationData) {
      handleNotificationNavigation(navigationData, navigate);
    } else {
      console.warn("No navigation data available for this notification");
    }

    // Reload notifications to update UI
    if (onReload) {
      onReload();
    }
  };

  /**
   * Handle delete button click
   */
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  return (
    <motion.div
      variants={rowVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={`relative flex items-start gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
        isUrgent && !isClicked
          ? `${displayConfig.bgColor} border-2 ${displayConfig.borderColor} ${
              displayConfig.animate ? "animate-pulse" : ""
            }`
          : isClicked
          ? "bg-gray-50 hover:bg-gray-100"
          : "bg-white hover:bg-gray-50 border border-gray-100"
      }`}
      onClick={handleNotificationClick}
    >
      {/* Notification Icon */}
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xl shadow-md">
          {notification.img_url ? (
            <img
              src={notification.img_url}
              alt="Notification"
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <span>{icon}</span>
          )}
        </div>
        
        {/* Badge indicator for notification type */}
        {!isClicked && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        )}
      </div>

      {/* Notification Content */}
      <div className="flex-1 min-w-0">
        {/* Title and Badge */}
        <div className="flex items-start gap-2 mb-1">
          <h3 className={`text-sm font-semibold flex-1 ${
            isClicked ? "text-gray-600" : "text-gray-900"
          }`}>
            {lang === "en"
              ? trimString(notification.title_en || notification.content_en, 60)
              : trimString(notification.title_ar || notification.content_ar, 60)}
          </h3>
          
          {displayConfig.badgeText && (
            <span
              className={`px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap ${
                displayConfig.badgeColor
              } ${displayConfig.animate ? "animate-pulse" : ""}`}
            >
              {displayConfig.badgeText}
            </span>
          )}
        </div>

        {/* Content */}
        <Tooltip
          content={
            lang === "en" ? notification.content_en : notification.content_ar
          }
        >
          <p className={`text-xs line-clamp-2 mb-2 ${
            isClicked ? "text-gray-500" : "text-gray-700"
          }`}>
            {lang === "en"
              ? trimString(notification.content_en, 120)
              : trimString(notification.content_ar, 120)}
          </p>
        </Tooltip>

        {/* Score display for graded exams */}
        {displayConfig.scoreInfo && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-600">Score:</span>
            <span className={`text-sm font-bold ${
              displayConfig.isPassed ? "text-green-600" : "text-red-600"
            }`}>
              {displayConfig.scoreInfo}
            </span>
            {displayConfig.percentage && (
              <span className="text-xs text-gray-500">
                ({displayConfig.percentage})
              </span>
            )}
            {displayConfig.passStatus && (
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                displayConfig.isPassed
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}>
                {displayConfig.passStatus}
              </span>
            )}
          </div>
        )}

        {/* Course status for course completion */}
        {displayConfig.statusEn && (
          <div className="mb-2">
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
              displayConfig.statusEn.includes('Honor')
                ? "bg-purple-100 text-purple-700"
                : displayConfig.statusEn.includes('Pass')
                ? "bg-green-100 text-green-700"
                : "bg-yellow-100 text-yellow-700"
            }`}>
              {lang === "en" ? displayConfig.statusEn : displayConfig.statusAr}
            </span>
          </div>
        )}

        {/* Deadline info for reminders */}
        {displayConfig.hoursRemaining !== undefined && (
          <div className="flex items-center gap-1 text-xs text-orange-600 mb-2">
            <span className="font-medium">⏰</span>
            <span>
              {displayConfig.hoursRemaining === 24
                ? "Deadline in 1 day"
                : `Deadline in ${displayConfig.hoursRemaining} hours`}
            </span>
          </div>
        )}

        {/* Action button */}
        {displayConfig.actionText && navigationData && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNotificationClick();
            }}
            className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-all duration-200 ${
              isUrgent
                ? "bg-red-500 hover:bg-red-600 shadow-md"
                : "bg-blue-500 hover:bg-blue-600"
            } ${displayConfig.animate ? "animate-pulse" : ""}`}
          >
            {displayConfig.actionText}
          </button>
        )}

        {/* Timestamp */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-gray-400">
            {notification.created_at}
          </span>
        </div>
      </div>

      {/* Delete button */}
      <button
        onClick={handleDeleteClick}
        className="flex-shrink-0 p-2 hover:bg-red-50 rounded-full transition-colors group"
        title="Delete notification"
      >
        <img
          src={Bin}
          className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity"
          alt="Delete"
        />
      </button>
    </motion.div>
  );
};

export default NotificationRow;
